import React, { useContext, useState } from 'react';
import {
    Alert,
    Image,
    Modal,
    Platform,
    SafeAreaView,
    ScrollView,
    StyleSheet,
    Text,
    TextInput,
    TouchableOpacity,
    View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as Haptics from 'expo-haptics';
import * as ImagePicker from 'expo-image-picker';
import * as ImageManipulator from 'expo-image-manipulator';
import { AuthContext } from '../context/AuthContext';
import Colors from '../constants/Colors';
import api from '../services/api';
import { showAlert } from '../services/confirm';

const theme = Colors.light;
const { spacing, radius } = Colors;

const Section = ({ title, children }) => (
    <View style={styles.sectionContainer}>
        {title ? <Text style={styles.sectionHeader}>{title}</Text> : null}
        <View style={styles.sectionBody}>{children}</View>
    </View>
);

const SettingItem = ({
    icon,
    color,
    label,
    value,
    onPress,
    isDestructive,
    isLast,
    disabled,
    showChevron,
}) => (
    <TouchableOpacity
        style={styles.itemContainer}
        onPress={() => {
            if (disabled) return;
            Haptics.selectionAsync();
            onPress?.();
        }}
        activeOpacity={disabled ? 1 : 0.7}
    >
        <View style={styles.itemLeft}>
            <View style={[
                styles.iconContainer,
                {
                    backgroundColor: disabled
                        ? theme.background
                        : (isDestructive ? 'rgba(255,59,48,0.1)' : (color || theme.secondaryLight)),
                },
            ]}
            >
                <Ionicons
                    name={icon}
                    size={18}
                    color={disabled ? theme.textTertiary : (isDestructive ? theme.destructive : '#FFFFFF')}
                />
            </View>
            <Text style={[
                styles.itemLabel,
                isDestructive && { color: theme.destructive },
                disabled && { color: theme.textTertiary },
            ]}
            >
                {label}
            </Text>
        </View>
        <View style={styles.itemRight}>
            {value ? <Text style={[styles.itemValue, disabled && { color: theme.textTertiary }]}>{value}</Text> : null}
            {((showChevron && !disabled) || (!value && !isDestructive && !disabled)) && (
                <Ionicons name="chevron-forward" size={20} color={theme.textTertiary} />
            )}
        </View>
        {!isLast && <View style={styles.itemSeparator} />}
    </TouchableOpacity>
);

export default function SettingsScreen() {
    const { user, logout, updateUser, refreshPartner } = useContext(AuthContext);
    const [nicknameModalVisible, setNicknameModalVisible] = useState(false);
    const [nicknameDraft, setNicknameDraft] = useState('');
    const [savingNickname, setSavingNickname] = useState(false);

    const confirmNative = (title, message, onConfirm, destructive = false) => {
        Alert.alert(title, message, [
            { text: '取消', style: 'cancel' },
            { text: '确认', style: destructive ? 'destructive' : 'default', onPress: onConfirm },
        ]);
    };

    const handleLogout = () => {
        Haptics.notificationAsync(Haptics.NotificationFeedbackType.Warning);
        if (Platform.OS === 'web') {
            if (window.confirm('确认退出当前账号？')) {
                logout();
            }
            return;
        }

        confirmNative('退出登录', '确认退出当前账号？', () => logout(), true);
    };

    const handleAvatarPress = async () => {
        const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
        if (status !== 'granted') {
            showAlert('需要权限', '请先允许访问相册后再更换头像。');
            return;
        }

        try {
            const result = await ImagePicker.launchImageLibraryAsync({
                mediaTypes: ImagePicker.MediaTypeOptions.Images,
                allowsEditing: false,
                quality: 1,
            });

            if (result.canceled) return;
            const pickedAsset = result.assets?.[0];
            if (!pickedAsset?.uri) return;

            const shouldUpload = Platform.OS === 'web'
                ? window.confirm('使用这张图片作为头像吗？')
                : await new Promise((resolve) => {
                    Alert.alert(
                        '上传头像',
                        '使用这张图片作为头像吗？',
                        [
                            { text: '取消', style: 'cancel', onPress: () => resolve(false) },
                            { text: '确认', onPress: () => resolve(true) },
                        ]
                    );
                });

            if (!shouldUpload) return;

            const originalWidth = Number(pickedAsset.width || 0);
            const originalHeight = Number(pickedAsset.height || 0);
            const cropSize = Math.min(originalWidth || 0, originalHeight || 0);
            const manipActions = [];

            if (cropSize > 0) {
                manipActions.push({
                    crop: {
                        originX: Math.max(0, (originalWidth - cropSize) / 2),
                        originY: Math.max(0, (originalHeight - cropSize) / 2),
                        width: cropSize,
                        height: cropSize,
                    },
                });
            }

            manipActions.push({ resize: { width: 300, height: 300 } });
            const manipResult = await ImageManipulator.manipulateAsync(
                pickedAsset.uri,
                manipActions,
                { compress: 0.7, format: ImageManipulator.SaveFormat.JPEG, base64: true }
            );

            const base64Img = `data:image/jpeg;base64,${manipResult.base64}`;
            await api.put('/user/profile', { avatar: base64Img });
            await updateUser({ avatar: base64Img });
            await Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
        } catch (error) {
            showAlert('错误', `头像更新失败：${error?.response?.data?.message || error?.message || '未知错误'}`);
        }
    };

    const handleRelationshipSwitch = () => {
        const current = user?.relationshipStatus || 'dating';
        const next = current === 'married' ? 'dating' : 'married';
        const nextLabel = next === 'married' ? '已婚' : '恋爱中';

        const submit = async () => {
            try {
                if (user?.partnerId) {
                    await api.post('/user/relationship/request', { status: next });
                    await Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
                    showAlert('已发送', `请求已发送，等待伴侣确认切换为“${nextLabel}”。`);
                } else {
                    await api.put('/user/profile', { relationshipStatus: next });
                    await updateUser({ relationshipStatus: next });
                    await Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
                }
            } catch (error) {
                showAlert('错误', `操作失败：${error?.response?.data?.message || '未知错误'}`);
            }
        };

        if (Platform.OS === 'web') {
            if (window.confirm(`确认切换关系模式为“${nextLabel}”？`)) {
                void submit();
            }
            return;
        }

        confirmNative(
            '切换模式',
            user?.partnerId
                ? `确认发起切换到“${nextLabel}”的请求？需要伴侣确认。`
                : `确认切换到“${nextLabel}”？`,
            () => { void submit(); }
        );
    };

    const handleUnpairRequest = () => {
        const submit = async () => {
            try {
                const response = await api.post('/user/pair/unpair/request');
                await Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
                showAlert('已发送', response?.data?.message || '解除配对请求已发送，等待对方确认。');
            } catch (error) {
                showAlert('发送失败', error?.response?.data?.message || '发送解除配对请求失败。');
            }
        };

        if (Platform.OS === 'web') {
            if (window.confirm('确认发送解除配对请求？该操作需要伴侣确认。')) {
                void submit();
            }
            return;
        }

        confirmNative(
            '解除配对',
            '确认发送解除配对请求？该操作需要伴侣确认。',
            () => { void submit(); },
            true
        );
    };

    const openNicknameModal = () => {
        setNicknameDraft(user?.nickname || user?.username || '');
        setNicknameModalVisible(true);
    };

    const closeNicknameModal = () => {
        if (!savingNickname) {
            setNicknameModalVisible(false);
        }
    };

    const handleSaveNickname = async () => {
        const nextNickname = nicknameDraft.trim();

        if (!nextNickname) {
            showAlert('提示', '昵称不能为空。');
            return;
        }
        if (nextNickname.length > 20) {
            showAlert('提示', '昵称最多 20 个字符。');
            return;
        }
        if (nextNickname === (user?.nickname || user?.username || '')) {
            closeNicknameModal();
            return;
        }

        try {
            setSavingNickname(true);
            await api.put('/user/profile', { nickname: nextNickname });
            await updateUser({ nickname: nextNickname });
            await refreshPartner();
            await Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
            setNicknameModalVisible(false);
        } catch (error) {
            showAlert('错误', error?.response?.data?.message || '更新昵称失败。');
        } finally {
            setSavingNickname(false);
        }
    };

    return (
        <SafeAreaView style={styles.container}>
            <ScrollView contentContainerStyle={styles.scrollContent}>
                <View style={styles.profileHeader}>
                    <TouchableOpacity onPress={handleAvatarPress} activeOpacity={0.8}>
                        {user?.avatar ? (
                            <Image source={{ uri: user.avatar }} style={styles.avatar} />
                        ) : (
                            <View style={styles.avatarPlaceholder}>
                                <Text style={styles.avatarText}>
                                    {(user?.nickname || user?.username || '我').charAt(0).toUpperCase()}
                                </Text>
                                <View style={styles.editBadge}>
                                    <Ionicons name="camera" size={12} color="#fff" />
                                </View>
                            </View>
                        )}
                    </TouchableOpacity>

                    <View style={styles.profileInfo}>
                        <Text style={styles.profileName}>{user?.nickname || user?.username}</Text>
                        <Text style={styles.profileDetail}>点击头像可更换照片</Text>
                    </View>
                </View>

                <Section>
                    <SettingItem
                        icon="person"
                        label="昵称"
                        value={user?.nickname || user?.username || '未设置'}
                        color="#5AC8FA"
                        onPress={openNicknameModal}
                        showChevron
                    />
                    <SettingItem
                        icon="heart"
                        label="关系模式"
                        value={user?.relationshipStatus === 'married' ? '已婚' : '恋爱中'}
                        color="#FF2D55"
                        onPress={handleRelationshipSwitch}
                    />
                    <SettingItem
                        icon="qr-code"
                        label="配对码"
                        value={user?.pairingCode}
                        color="#8E8E93"
                        isLast={!user?.partnerId}
                    />
                    {user?.partnerId && (
                        <SettingItem
                            icon="unlink"
                            label="解除配对（需确认）"
                            isDestructive
                            onPress={handleUnpairRequest}
                            isLast
                        />
                    )}
                </Section>

                <Section>
                    <SettingItem
                        icon="notifications"
                        label="通知"
                        color="#FF9500"
                        disabled
                        onPress={() => {}}
                    />
                    <SettingItem
                        icon="shield-checkmark"
                        label="隐私与安全"
                        color="#34C759"
                        disabled
                        onPress={() => {}}
                        isLast
                    />
                </Section>

                <Section>
                    <SettingItem
                        icon="information-circle"
                        label="关于"
                        value="v1.0.0"
                        color="#007AFF"
                        onPress={() => {}}
                    />
                    <SettingItem
                        icon="log-out"
                        label="退出登录"
                        isDestructive
                        onPress={handleLogout}
                        isLast
                    />
                </Section>
            </ScrollView>

            <Modal
                visible={nicknameModalVisible}
                transparent
                animationType="fade"
                onRequestClose={closeNicknameModal}
            >
                <View style={styles.modalBackdrop}>
                    <View style={styles.modalCard}>
                        <Text style={styles.modalTitle}>修改昵称</Text>
                        <TextInput
                            value={nicknameDraft}
                            onChangeText={setNicknameDraft}
                            placeholder="输入昵称"
                            placeholderTextColor={theme.textTertiary}
                            maxLength={20}
                            style={styles.modalInput}
                            autoFocus
                        />
                        <View style={styles.modalActions}>
                            <TouchableOpacity style={styles.modalBtn} onPress={closeNicknameModal} disabled={savingNickname}>
                                <Text style={styles.modalBtnText}>取消</Text>
                            </TouchableOpacity>
                            <TouchableOpacity
                                style={[styles.modalBtn, styles.modalBtnPrimary, savingNickname && styles.modalBtnDisabled]}
                                onPress={handleSaveNickname}
                                disabled={savingNickname}
                            >
                                <Text style={[styles.modalBtnText, styles.modalBtnPrimaryText]}>
                                    {savingNickname ? '保存中...' : '保存'}
                                </Text>
                            </TouchableOpacity>
                        </View>
                    </View>
                </View>
            </Modal>
        </SafeAreaView>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: theme.backgroundGrouped,
    },
    scrollContent: {
        paddingVertical: spacing.xl,
    },
    profileHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: spacing.xxl,
        paddingHorizontal: spacing.lg,
    },
    avatar: {
        width: 64,
        height: 64,
        borderRadius: 32,
        backgroundColor: '#E1E1E1',
    },
    avatarPlaceholder: {
        width: 64,
        height: 64,
        borderRadius: 32,
        backgroundColor: '#E1E1E1',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
    },
    avatarText: {
        fontSize: 26,
        fontWeight: '600',
        color: '#999',
    },
    editBadge: {
        position: 'absolute',
        bottom: 0,
        right: 0,
        backgroundColor: theme.primary,
        width: 20,
        height: 20,
        borderRadius: 10,
        justifyContent: 'center',
        alignItems: 'center',
        borderWidth: 2,
        borderColor: theme.backgroundGrouped,
    },
    profileInfo: {
        marginLeft: spacing.lg,
        flex: 1,
    },
    profileName: {
        fontSize: 22,
        fontWeight: '700',
        color: theme.text,
        marginBottom: 4,
    },
    profileDetail: {
        fontSize: 15,
        color: theme.textSecondary,
    },
    sectionContainer: {
        marginBottom: spacing.xl,
        backgroundColor: theme.backgroundSecondary,
        borderTopWidth: StyleSheet.hairlineWidth,
        borderBottomWidth: StyleSheet.hairlineWidth,
        borderColor: theme.border,
    },
    sectionHeader: {
        paddingHorizontal: spacing.lg,
        paddingBottom: spacing.xs,
        fontSize: 13,
        fontWeight: '400',
        color: theme.textSecondary,
        textTransform: 'uppercase',
        marginLeft: spacing.lg,
    },
    sectionBody: {},
    itemContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingVertical: 12,
        paddingHorizontal: spacing.lg,
        backgroundColor: theme.backgroundSecondary,
        minHeight: 50,
        position: 'relative',
    },
    itemSeparator: {
        position: 'absolute',
        bottom: 0,
        left: 60,
        right: 0,
        height: StyleSheet.hairlineWidth,
        backgroundColor: theme.separator,
    },
    itemLeft: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
    },
    iconContainer: {
        width: 30,
        height: 30,
        borderRadius: 6,
        justifyContent: 'center',
        alignItems: 'center',
    },
    itemLabel: {
        fontSize: 17,
        color: theme.text,
    },
    itemRight: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
    },
    itemValue: {
        fontSize: 17,
        color: theme.textSecondary,
    },
    modalBackdrop: {
        flex: 1,
        backgroundColor: 'rgba(0,0,0,0.28)',
        justifyContent: 'center',
        paddingHorizontal: spacing.lg,
    },
    modalCard: {
        backgroundColor: theme.backgroundSecondary,
        borderRadius: radius.card,
        padding: spacing.lg,
    },
    modalTitle: {
        fontSize: 18,
        fontWeight: '700',
        color: theme.text,
    },
    modalInput: {
        marginTop: spacing.md,
        height: 44,
        borderWidth: 1,
        borderColor: theme.border,
        borderRadius: radius.md,
        paddingHorizontal: 12,
        fontSize: 16,
        color: theme.text,
        backgroundColor: theme.background,
    },
    modalActions: {
        marginTop: spacing.lg,
        flexDirection: 'row',
        justifyContent: 'flex-end',
        gap: spacing.sm,
    },
    modalBtn: {
        minWidth: 72,
        height: 38,
        borderRadius: radius.md,
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: 12,
        backgroundColor: theme.background,
    },
    modalBtnPrimary: {
        backgroundColor: theme.primary,
    },
    modalBtnDisabled: {
        opacity: 0.6,
    },
    modalBtnText: {
        fontSize: 14,
        color: theme.text,
        fontWeight: '600',
    },
    modalBtnPrimaryText: {
        color: '#fff',
    },
});
